import { Link } from "react-router-dom";
import { GraduationCap, Volleyball, HeartPulse, Users, Lightbulb, HeartHandshake } from "lucide-react";
import { Hero } from "@/components/Hero";
import { SecuritySection } from "@/components/SecuritySection";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { CountUp } from "@/components/CountUp";
import { IMAGES, STATS } from "@/data/content";

const FOCUS = [
  { icon: GraduationCap, title: "Education", text: "Scholarships, STEM, workforce readiness, and lifelong learning." },
  { icon: Volleyball, title: "Athletics", text: "Youth sports, adaptive athletics, equipment assistance, and coach development." },
  { icon: HeartPulse, title: "Health & Wellness", text: "Promoting physical health, mental wellness, nutrition, and injury prevention." },
  { icon: Users, title: "Leadership", text: "Developing character, mentorship, civic engagement, and future leaders." },
  { icon: Lightbulb, title: "Innovation", text: "Supporting innovation, research, technology access, and community advancement." },
];

const STRIP = [
  { src: IMAGES.stripStudy, alt: "Students studying" },
  { src: IMAGES.stripHands, alt: "Hands together in unity" },
  { src: IMAGES.stripVolleyball, alt: "Young athlete on court" },
  { src: IMAGES.stripReading, alt: "Child reading" },
];

export default function Home() {
  return (
    <>
      <Hero />

      {/* Mission */}
      <section className="brand-cream txt-ink relative z-10 -mt-1 lg:mt-0" data-testid="mission">
        <div className="mx-auto max-w-4xl px-5 pb-20 pt-6 text-center md:px-8 md:pb-24">
          <Reveal>
            <p className="overline mx-auto text-[#c8892b]">Our Mission</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mx-auto mt-6 max-w-3xl font-heading text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
              To expand access to education, athletics, health and wellness, leadership development,
              and innovation.
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mx-auto mt-6 max-w-2xl leading-relaxed text-[#0d1d47]/70">
              Through scholarships, grants, programs, and community partnerships, we empower
              individuals to reach their fullest potential and create lasting impact in the
              communities we serve.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Focus Areas */}
      <section className="brand-ink text-white" data-testid="focus-areas">
        <div className="mx-auto max-w-[1400px] px-5 py-16 md:px-8 md:py-20">
          <Reveal>
            <h2 className="mb-14 text-center font-heading text-xl font-bold uppercase tracking-[0.18em] txt-gold">
              Our Focus Areas
            </h2>
          </Reveal>
          <Stagger className="grid grid-cols-1 gap-y-12 sm:grid-cols-2 lg:grid-cols-5 lg:gap-y-0">
            {FOCUS.map((f, i) => (
              <StaggerItem key={f.title}>
                <div className={`flair-card rounded-xl px-6 py-5 text-center ${i !== 0 ? "lg:border-l lg:border-white/15" : ""}`}>
                  <f.icon className="flair-icon mx-auto h-12 w-12 txt-gold" strokeWidth={1.4} />
                  <h3 className="mt-5 font-heading text-lg font-bold uppercase tracking-wide txt-gold">{f.title}</h3>
                  <p className="mx-auto mt-3 max-w-[15rem] text-sm leading-relaxed text-white/75">{f.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Impact stats (animated) */}
      <section className="brand-cream txt-ink" data-testid="stats-band">
        <div className="mx-auto max-w-[1400px] px-5 py-16 md:px-8 md:py-20">
          <Stagger className="grid grid-cols-2 gap-y-10 lg:grid-cols-4">
            {STATS.map((s) => (
              <StaggerItem key={s.label}>
                <div className="border-l-2 border-[#c8892b] pl-5 text-center sm:text-left">
                  <div className="font-heading text-4xl font-extrabold tracking-tight md:text-5xl">
                    {typeof s.value === "number" ? (
                      <CountUp to={s.value} suffix={s.suffix} />
                    ) : (
                      <span>{s.value}{s.suffix}</span>
                    )}
                  </div>
                  <div className="mt-2 font-heading text-sm font-bold uppercase tracking-wide txt-gold">{s.label}</div>
                  <div className="text-xs text-[#0d1d47]/60">{s.sub}</div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Photo strip */}
      <section className="brand-ink">
        <div className="grid grid-cols-2 md:grid-cols-4">
          {STRIP.map((s, i) => (
            <div key={i} className="relative aspect-[4/3] overflow-hidden">
              <img src={s.src} alt={s.alt} className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
            </div>
          ))}
        </div>
      </section>

      {/* CTA band */}
      <section className="brand-cream txt-ink" data-testid="closing-cta">
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 px-5 py-16 md:px-8 md:py-20 lg:grid-cols-[1.1fr_auto_1fr] lg:gap-10">
          <Reveal>
            <div className="flex items-start gap-6 border-l-2 border-[#c8892b] pl-5 lg:border-l-0 lg:pl-0">
              <HeartHandshake className="hidden h-16 w-16 shrink-0 txt-gold sm:block" strokeWidth={1.3} />
              <div>
                <h3 className="font-heading text-2xl font-bold tracking-tight">Together, we can</h3>
                <p className="mt-1 font-heading text-2xl font-extrabold uppercase tracking-tight txt-gold">
                  Inspire. Equip. Empower.
                </p>
                <p className="mt-2 text-sm font-semibold uppercase tracking-wide text-[#0d1d47]/70">
                  Helping today. Transforming tomorrow.
                </p>
              </div>
            </div>
          </Reveal>

          <div className="hidden h-24 w-px bg-[#0d1d47]/15 lg:block" />

          <Reveal delay={0.1}>
            <div className="border-l-2 border-[#c8892b] pl-5 lg:border-l-0 lg:pl-0">
              <h3 className="font-heading text-2xl font-bold tracking-tight">Be part of the impact.</h3>
              <p className="mt-2 text-[#0d1d47]/70">
                Your support helps us create opportunity and change lives.
              </p>
              <p className="mt-3 font-heading text-lg font-extrabold uppercase tracking-tight txt-gold">
                Donate. Volunteer. Partner.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/donate" data-testid="cta-donate" className="inline-flex items-center gap-2 rounded-md brand-gold-bg px-6 py-3 text-sm font-bold uppercase tracking-wide text-[#0d1d47] transition-transform hover:-translate-y-0.5">
                  Donate
                </Link>
                <Link to="/get-involved" data-testid="cta-involved" className="inline-flex items-center gap-2 rounded-md border border-[#0d1d47]/25 px-6 py-3 text-sm font-bold uppercase tracking-wide text-[#0d1d47] transition-colors hover:border-[#c8892b] hover:text-[#c8892b]">
                  Get Involved
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <SecuritySection />
    </>
  );
}
